import React, { useRef, useEffect, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Phone, MapPin, Navigation, Building, X } from 'lucide-react';
import { outlets } from '../data/mock';
import { Button } from './ui/button';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';

gsap.registerPlugin(ScrollTrigger);

// Fix for default marker icons in Leaflet with webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom marker icons for different zones
const createCustomIcon = (color, isHeadOffice = false) => {
  const size = isHeadOffice ? 40 : 30;
  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div style="
        width: ${size}px;
        height: ${size}px;
        background: ${color};
        border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        border: 3px solid white;
        box-shadow: 0 4px 10px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
      ">
        <div style="
          transform: rotate(45deg);
          color: white;
          font-size: ${isHeadOffice ? '16px' : '12px'};
          font-weight: bold;
        ">🍕</div>
      </div>
    `,
    iconSize: [size, size],
    iconAnchor: [size / 2, size],
    popupAnchor: [0, -size],
  });
};

const defaultColors = ['#ef4444', '#f59e0b', '#8b5cf6', '#3b82f6', '#10b981', '#ec4899', '#14b8a6', '#f43f5e', '#6366f1'];

const getZoneColor = (zone) => {
  const fixedColors = {
    'East Pune': '#ef4444',
    'West Pune': '#f59e0b',
    'South Pune': '#8b5cf6',
    'PCMC': '#3b82f6',
  };
  if (fixedColors[zone]) return fixedColors[zone];
  if (!zone) return '#6b7280';
  let hash = 0;
  for (let i = 0; i < zone.length; i++) {
    hash = zone.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % defaultColors.length;
  return defaultColors[index];
};

// Component to fit bounds when outlets change
const FitBounds = ({ outlets }) => {
  const map = useMap();

  useEffect(() => {
    if (outlets.length > 0) {
      const bounds = L.latLngBounds(outlets.map(o => [o.lat, o.lng]));
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [outlets, map]);

  return null;
};

// Component to fly to a location
const FlyToLocation = ({ location }) => {
  const map = useMap();

  useEffect(() => {
    if (location) {
      map.flyTo([location.lat, location.lng], 15, {
        duration: 1.5
      });
    }
  }, [location, map]);

  return null;
};

// Component to control map interactions
const MapInteractionController = ({ interactive }) => {
  const map = useMap();
  useEffect(() => {
    if (interactive) {
      map.dragging.enable();
      map.touchZoom.enable();
      map.scrollWheelZoom.enable();
      map.doubleClickZoom.enable();
      map.boxZoom.enable();
      map.keyboard.enable();
      if (map.tap) map.tap.enable();
    } else {
      map.dragging.disable();
      map.touchZoom.disable();
      map.scrollWheelZoom.disable();
      map.doubleClickZoom.disable();
      map.boxZoom.disable();
      map.keyboard.disable();
      if (map.tap) map.tap.disable();
    }
  }, [interactive, map]);
  return null;
};

import { useOutlets } from '../hooks/useOutlets';

const InteractiveMap = () => {
  const sectionRef = useRef(null);
  const { language } = useLanguage();
  const t = translations[language];
  const { outlets } = useOutlets();
  const [selectedOutlet, setSelectedOutlet] = useState(null);
  const [activeZone, setActiveZone] = useState('All');
  const [flyToOutlet, setFlyToOutlet] = useState(null);
  const [isMapInteractive, setIsMapInteractive] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const uniqueZones = Array.from(new Set(outlets.map(o => o.zone).filter(Boolean)));

  const getZoneName = (zone, lang) => {
    if (lang === 'mr') {
      const translations = {
        'East Pune': 'पूर्व पुणे',
        'West Pune': 'पश्चिम पुणे',
        'South Pune': 'दक्षिण पुणे',
        'PCMC': 'पिंपरी-चिंचवड (PCMC)',
        'Central Pune': 'मध्य पुणे'
      };
      return translations[zone] || zone;
    }
    return zone;
  };

  const zones = [
    { name: t.outlets.allZones, value: 'All', color: '#6b7280' },
    ...uniqueZones.map(zone => ({
      name: getZoneName(zone, language),
      value: zone,
      color: getZoneColor(zone)
    }))
  ];

  const filteredOutlets = outlets.filter(o => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = 
      o.name.toLowerCase().includes(searchLower) || 
      (o.address || '').toLowerCase().includes(searchLower);
    
    if (searchTerm) return matchesSearch;
    return activeZone === 'All' || o.zone === activeZone;
  });


  const handleOutletClick = (outlet) => {
    setSelectedOutlet(outlet);
    setFlyToOutlet(outlet);
    setIsMapInteractive(true);
  };

  const handleCallNow = (phone) => {
    if (phone) {
      window.location.href = `tel:${phone}`;
    }
  };

  const handleGetDirections = (outlet) => {
    const url = `https://www.google.com/maps/dir/?api=1&destination=${outlet.lat},${outlet.lng}`;
    window.open(url, '_blank');
  };

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo('.map-container',
        { opacity: 0, scale: 0.95 },
        {
          opacity: 1,
          scale: 1,
          duration: 1,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 70%'
          }
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, [language]);

  // Center of Pune
  const puneCenter = [18.5204, 73.8567];

  return (
    <section id="outlets" ref={sectionRef} className="py-20 lg:py-32 bg-white dark:bg-black transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 px-4 py-2 rounded-full text-sm font-semibold mb-4" style={{ fontFamily: "'Poppins', sans-serif" }}>
            {t.outlets.badge}
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6" style={{ fontFamily: "'Poppins', sans-serif" }}>
            {t.outlets.title} <span className="text-red-600 dark:text-red-500">{t.outlets.highlight}</span>
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto" style={{ fontFamily: "'Poppins', sans-serif" }}>
            {t.outlets.subtitle}
          </p>
        </div>

        {/* Zone Filter and Search */}
        <div className="mb-8 flex flex-col md:flex-row gap-4 items-center justify-between">
          <div className="flex flex-wrap gap-2 justify-center md:justify-start">
            {zones.map((zone) => (
              <button
                key={zone.value}
                onClick={() => {
                  setActiveZone(zone.value);
                  setSelectedOutlet(null);
                  setFlyToOutlet(null);
                }}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${activeZone === zone.value
                  ? 'text-white shadow-lg scale-105'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 shadow'
                  }`}
                style={{
                  backgroundColor: activeZone === zone.value ? zone.color : undefined,
                  fontFamily: "'Poppins', sans-serif"
                }}
              >
                {zone.name}
              </button>
            ))}
          </div>
          <div className="w-full md:w-64">
            <input
              type="text"
              placeholder={t.outlets.searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 rounded-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500 transition-all"
              style={{ fontFamily: "'Poppins', sans-serif" }}
            />
          </div>
        </div>

        <div className="map-container grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Outlet List */}
          <div className="lg:col-span-1 bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden transition-colors duration-300">
            <div className="p-4 bg-gradient-to-r from-red-500 to-red-600 text-white">
              <h3 className="text-lg font-bold" style={{ fontFamily: "'Poppins', sans-serif" }}>
                📍 {t.outlets.ourOutlets}
              </h3>
              <p className="text-sm text-white/80" style={{ fontFamily: "'Poppins', sans-serif" }}>
                {t.outlets.viewOnMap}
              </p>
            </div>
            <div className="max-h-[300px] lg:max-h-[500px] overflow-y-auto">
              {filteredOutlets.map((outlet) => (
                <div
                  key={outlet.id}
                  onClick={() => handleOutletClick(outlet)}
                  className={`p-4 border-b border-gray-100 dark:border-gray-700 cursor-pointer transition-all duration-200 hover:bg-red-50 dark:hover:bg-gray-700 ${selectedOutlet?.id === outlet.id ? 'bg-red-50 dark:bg-gray-700 border-l-4 border-l-red-500' : ''
                    }`}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className="w-3 h-3 rounded-full mt-1.5 flex-shrink-0"
                      style={{ backgroundColor: getZoneColor(outlet.zone) }}
                    ></div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h4 className="font-semibold text-gray-900 dark:text-white text-sm" style={{ fontFamily: "'Poppins', sans-serif" }}>
                          {outlet.name}
                          {outlet.isHeadOffice && (
                            <span className="ml-2 text-xs bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 px-2 py-0.5 rounded-full">
                              Head Office
                            </span>
                          )}
                        </h4>
                        <a 
                          href={`/${outlet.slug}`} 
                          onClick={(e) => e.stopPropagation()} 
                          className="text-[10px] text-red-500 font-bold hover:underline uppercase tracking-tighter"
                        >
                          View Page
                        </a>
                      </div>
                      {outlet.phone && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1" style={{ fontFamily: "'Poppins', sans-serif" }}>
                          📞 {outlet.phone}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Map Container */}
          <div className="lg:col-span-2 relative">
            {!isMapInteractive && (
              <div
                className="absolute inset-0 z-[500] bg-black/5 flex items-center justify-center cursor-pointer hover:bg-black/10 transition-colors rounded-2xl"
                onClick={() => setIsMapInteractive(true)}
                onTouchEnd={() => setIsMapInteractive(true)}
              >
                <div className="bg-white/95 px-5 py-2.5 rounded-full shadow-xl text-sm font-bold text-gray-800 flex items-center gap-2 transform transition-transform hover:scale-105 border border-gray-100">
                  <span>👆</span> Tap to explore map
                </div>
              </div>
            )}
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden" style={{ height: '550px' }}>
              <MapContainer
                center={puneCenter}
                zoom={11}
                style={{ height: '100%', width: '100%' }}
                scrollWheelZoom={false}
                dragging={false}
                touchZoom={false}
                doubleClickZoom={false}
              >
                <MapInteractionController interactive={isMapInteractive} />
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />

                <FitBounds outlets={filteredOutlets} />
                {flyToOutlet && <FlyToLocation location={flyToOutlet} />}

                {filteredOutlets.map((outlet) => (
                  <Marker
                    key={outlet.id}
                    position={[outlet.lat, outlet.lng]}
                    icon={createCustomIcon(getZoneColor(outlet.zone), outlet.isHeadOffice)}
                    eventHandlers={{
                      click: () => setSelectedOutlet(outlet)
                    }}
                  >
                    <Popup>
                      <div className="p-2 min-w-[200px]" style={{ fontFamily: "'Poppins', sans-serif" }}>
                        <h3 className="font-bold text-gray-900 text-base mb-2">
                          {outlet.name}
                        </h3>
                        <div className="space-y-2 text-sm">
                          <p className="flex items-start gap-2 text-gray-600">
                            <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-red-500" />
                            {outlet.address}
                          </p>
                          {outlet.phone && (
                            <p className="flex items-center gap-2 text-gray-600">
                              <Phone className="w-4 h-4 text-green-500" />
                              <a href={`tel:${outlet.phone}`} className="text-blue-600 hover:underline">
                                {outlet.phone}
                              </a>
                            </p>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-2 mt-3">
                          {outlet.phone && (
                            <button
                              onClick={() => handleCallNow(outlet.phone)}
                              className="flex-1 bg-green-500 hover:bg-green-600 text-white text-xs py-2 px-3 rounded-lg flex items-center justify-center gap-1 min-w-[80px]"
                            >
                              <Phone className="w-3 h-3" /> {t.outlets.call}
                            </button>
                          )}
                          <button
                            onClick={() => handleGetDirections(outlet)}
                            className="flex-1 bg-blue-500 hover:bg-blue-600 text-white text-xs py-2 px-3 rounded-lg flex items-center justify-center gap-1 min-w-[80px]"
                          >
                            <Navigation className="w-3 h-3" /> {t.outlets.directions}
                          </button>
                          <a 
                            href={`/${outlet.slug}`}
                            className="flex-1 bg-red-500 hover:bg-red-600 text-white text-xs py-2 px-3 rounded-lg flex items-center justify-center gap-1 min-w-[80px]"
                          >
                            <Building className="w-3 h-3" /> Visit Page
                          </a>
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>

            {/* Zone Legend */}
            <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur-sm rounded-xl p-3 shadow-lg z-[1000]">
              <p className="text-xs font-semibold text-gray-700 mb-2" style={{ fontFamily: "'Poppins', sans-serif" }}>
                {t.outlets.zoneLegend}
              </p>
              <div className="space-y-1">
                {zones.slice(1).map((zone) => (
                  <div key={zone.value} className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: zone.color }}></div>
                    <span className="text-xs text-gray-600" style={{ fontFamily: "'Poppins', sans-serif" }}>{zone.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Selected Outlet Detail Card */}
        {selectedOutlet && (
          <div className="mt-8 bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden animate-fade-in transition-colors duration-300">
            <div className="bg-gradient-to-r from-red-500 to-yellow-500 p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center">
                    <Building className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white" style={{ fontFamily: "'Poppins', sans-serif" }}>
                      {selectedOutlet.name}
                    </h3>
                    <p className="text-white/80" style={{ fontFamily: "'Poppins', sans-serif" }}>
                      {selectedOutlet.zone}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedOutlet(null)}
                  className="p-2 hover:bg-white/20 rounded-full transition-colors"
                >
                  <X className="w-6 h-6 text-white" />
                </button>
              </div>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400" style={{ fontFamily: "'Poppins', sans-serif" }}>{t.outlets.address}</p>
                    <p className="font-medium text-gray-900 dark:text-white" style={{ fontFamily: "'Poppins', sans-serif" }}>
                      {selectedOutlet.address}
                    </p>
                  </div>
                </div>
                {selectedOutlet.phone && (
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-green-600 dark:text-green-400" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400" style={{ fontFamily: "'Poppins', sans-serif" }}>{t.outlets.contact}</p>
                      <a
                        href={`tel:${selectedOutlet.phone}`}
                        className="font-medium text-gray-900 dark:text-white hover:text-red-600 dark:hover:text-red-400 transition-colors"
                        style={{ fontFamily: "'Poppins', sans-serif" }}
                      >
                        {selectedOutlet.phone}
                      </a>
                    </div>
                  </div>
                )}
              </div>
              <div className="flex gap-4 mt-6">
                {selectedOutlet.phone && (
                  <Button
                    onClick={() => handleCallNow(selectedOutlet.phone)}
                    className="flex-1 bg-green-500 hover:bg-green-600 text-white"
                    style={{ fontFamily: "'Poppins', sans-serif" }}
                  >
                    <Phone className="w-4 h-4 mr-2" /> {t.outlets.callNow}
                  </Button>
                )}
                <Button
                  onClick={() => handleGetDirections(selectedOutlet)}
                  className="flex-1 bg-blue-500 hover:bg-blue-600 text-white"
                  style={{ fontFamily: "'Poppins', sans-serif" }}
                >
                  <Navigation className="w-4 h-4 mr-2" /> {t.outlets.getDirections}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Stats Summary */}
        <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4">
          {zones.slice(1).map((zone) => (
            <div key={zone.value} className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-md text-center transition-colors duration-300">
              <div
                className="w-4 h-4 rounded-full mx-auto mb-2"
                style={{ backgroundColor: zone.color }}
              ></div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white" style={{ fontFamily: "'Poppins', sans-serif" }}>
                {outlets.filter(o => o.zone === zone.value).length}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400" style={{ fontFamily: "'Poppins', sans-serif" }}>
                {zone.name}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default InteractiveMap;
